import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { AlertProvider } from './context/AlertContext';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Home from './pages/Home';
import Feed from './pages/Feed';
import UploadActivity from './pages/UploadActivity';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Activity from './pages/Activity';
import Simulator from './pages/Simulator';
import Activities from './pages/Activities';
import CookiesPolicy from './pages/CookiesPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import { Box } from '@mui/material';

/* ─── Layout interior (necesita useLocation, dentro del Router) ── */
const AppLayout = ({ theme, actualTheme }) => {
  const location = useLocation();
  const isHome = location.pathname === '/';

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />

      {/* Wrapper raíz — en Home no ponemos background para que no se cuele */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          background: isHome ? 'transparent' : (actualTheme === 'dark' ? '#121212' : '#f5f5f5'),
          overflowX: 'hidden',
        }}
      >
        <Header />

        {/*
          MUI AppBar con position="fixed" no ocupa espacio en el flujo.
          MUI lo compensa con un <Toolbar> vacío automático si usas
          la utilidad "toolbar mixin". Lo hacemos manualmente solo
          para las páginas que NO son Home (Home ya gestiona su propio
          padding-top interno en el hero).
        */}
        {!isHome && (
          <Box sx={{ height: { xs: '56px', sm: '64px' } }} />
        )}

        {/* Contenido principal */}
        <Box
          component="main"
          sx={
            isHome
              ? {
                  /* Home: absolutamente sin padding ni margen */
                  flex: '1 0 auto',
                  p: 0,
                  m: 0,
                  width: '100%',
                  /* El Home gestiona su propio scroll interno */
                  overflow: 'visible',
                }
              : {
                  /* Resto de páginas: layout normal */
                  flex: '1 0 auto',
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  pb: { xs: 12, sm: 10 },
                }
          }
        >
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            <Route path="/cookies-policy" element={<CookiesPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />

            <Route path="/profile" element={<Profile />} />
            <Route path="/settings" element={<Settings />} />

            <Route path="/feed" element={<Feed />} />
            <Route path="/upload" element={<UploadActivity />} />
            <Route path="/activity" element={<Activities />} />
            <Route path="/activity/:id" element={<Activity />} />
            <Route path="/simulator" element={<Simulator />} />
          </Routes>
        </Box>

        {/* Footer — en Home lo ocultamos porque Home ya tiene su propio bloque de contacto */}
        {!isHome && (
          <Box sx={{ zIndex: 2, position: 'relative' }}>
            <Footer />
          </Box>
        )}
      </Box>
    </MuiThemeProvider>
  );
};

/* ─── AppContent ─────────────────────────────────────────────────── */
const AppContent = () => {
  const { actualTheme } = useTheme();

  const theme = createTheme({
    palette: {
      mode: actualTheme,
      primary: { main: '#2196f3' },
      background: {
        default: actualTheme === 'dark' ? '#121212' : '#f5f5f5',
        paper:   actualTheme === 'dark' ? '#1e1e1e' : '#ffffff',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeightLight:   300,
      fontWeightRegular: 400,
      fontWeightMedium:  500,
      fontWeightBold:    700,
    },
    components: {
      MuiButton: {
        styleOverrides: { root: { textTransform: 'none' } },
      },
      MuiCssBaseline: {
        styleOverrides: {
          /* Garantiza que no hay margin en html/body */
          html: { margin: 0, padding: 0 },
          body: { margin: 0, padding: 0, overflowX: 'hidden' },
        },
      },
    },
  });

  return <AppLayout theme={theme} actualTheme={actualTheme} />;
};

/* ─── App ────────────────────────────────────────────────────────── */
const App = () => {
  return (
    <ThemeProvider>
      {/* Router envuelve AppContent para que AppLayout pueda usar useLocation */}
      <Router>
        <AuthProvider>
          <AlertProvider>
            <AppContent />
          </AlertProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
};

export default App;